import { createDemoLearner } from './helpers'
import { SeededRng } from '../../utils/deterministicRng'
import { AppState } from '../../app/types'
import { LearnerAccount } from '../../accounts/types'
import { ACHIEVEMENTS } from '../../mascot/achievements/achievementData'
import {
  MODULES,
  getFamiliesForModule,
  getAllowedTiersForGroup,
} from '../../content'
import type { GroupId } from '../../config/systemConfig'

/**
 * Build a COMPLETE mastery map for a full-access demo profile.
 *
 * Walks every content module and every family that is eligible for the given
 * group (tier within the group's allowed range — the same filter the canonical
 * `getModuleMasteryFromFamilies` aggregator uses) and assigns a high mastery
 * score (96-99). Because the module-mastery aggregate averages over *attempted*
 * eligible families, covering every eligible family guarantees each module reads
 * ≥ 95% → `masteredCount` = all modules. That deterministically unlocks the
 * mastery-gated collectibles (Royal / Rainbow variants, Master tier, Frost),
 * so the Demo Djay locker shows a genuinely complete collection rather than a
 * hand-picked subset that could drift as the taxonomy grows.
 *
 * Deterministic: scores come from the shared seeded RNG so the demo state is
 * byte-identical on every load.
 */
function buildCompleteMasteryForGroup(
  group: GroupId,
  rng: SeededRng,
): Record<string, number> {
  const scores: Record<string, number> = {}
  const allowedTiers = getAllowedTiersForGroup(group)
  MODULES.forEach((module) => {
    getFamiliesForModule(module.id)
      .filter((family) => allowedTiers.has(family.tier))
      .forEach((family) => {
        scores[family.familyId] = rng.int(96, 99)
      })
  })
  return scores
}

/**
 * Persona shape for a group-aware learner mastery build.
 *
 * `strongModules` / `weakModules` are matched against `module.id` (the same
 * ids used as keys in `completedQuestions`, e.g. `Getalbegrip`, `SpellingWW`).
 */
interface PersonaProfile {
  /** Center of the learner's mastery distribution (0-100). */
  baseline: number
  /** +/- random variation applied per family. */
  spread: number
  /** Fraction (0-1) of eligible families the learner has actually attempted. */
  coverage: number
  /** Modules where the learner excels (+bonus). */
  strongModules?: string[]
  /** Modules where the learner struggles (−penalty). */
  weakModules?: string[]
}

/**
 * Build a GROUP-SCOPED, persona-shaped mastery map.
 *
 * Why this exists: the live module-mastery aggregator only averages families
 * whose tier falls inside the learner's group band (group 6 → tiers 1-6,
 * group 7 → 7-12, group 8 → 13-16). Hand-picked family lists routinely named
 * out-of-band families (e.g. a foundational tier-2 family on a group-8
 * learner), which the engine silently dropped — so the seeded "story" never
 * rendered in the UI.
 *
 * This generator walks only the families that are eligible for the learner's
 * group, then assigns a score driven by the persona (baseline competence,
 * per-module strength/weakness, coverage). Result: every score is guaranteed
 * in-band AND the learner keeps a recognisable profile (a taal-specialist still
 * peaks on spelling/taal, a struggling learner still reads low everywhere).
 *
 * Un-attempted families are intentionally omitted from the map (coverage < 1),
 * mirroring real partial progress rather than a perfectly filled grid.
 *
 * Deterministic via the shared seeded RNG.
 */
function buildPersonaMasteryForGroup(
  group: GroupId,
  rng: SeededRng,
  profile: PersonaProfile,
): Record<string, number> {
  const scores: Record<string, number> = {}
  const allowedTiers = getAllowedTiersForGroup(group)
  const strong = new Set(profile.strongModules ?? [])
  const weak = new Set(profile.weakModules ?? [])

  MODULES.forEach((module) => {
    const moduleBonus = strong.has(module.id)
      ? 14
      : weak.has(module.id)
        ? -18
        : 0

    getFamiliesForModule(module.id)
      .filter((family) => allowedTiers.has(family.tier))
      .forEach((family) => {
        // Decide whether this learner has attempted this family at all.
        if (!rng.chance(profile.coverage)) return

        const variation = rng.int(-profile.spread, profile.spread)
        const raw = profile.baseline + moduleBonus + variation
        // Clamp into a sensible 5-99 window.
        scores[family.familyId] = Math.max(5, Math.min(99, raw))
      })
  })

  return scores
}

export function createAllLearners(rng: SeededRng): {
  djayden: { account: LearnerAccount; data: AppState }
  emma: { account: LearnerAccount; data: AppState }
  noah: { account: LearnerAccount; data: AppState }
  sophie: { account: LearnerAccount; data: AppState }
  liam: { account: LearnerAccount; data: AppState }
  noor: { account: LearnerAccount; data: AppState }
  yara: { account: LearnerAccount; data: AppState }
  sem: { account: LearnerAccount; data: AppState }
  demo: { account: LearnerAccount; data: AppState }
} {
  // Djayden — Power user, level 8 (5850 XP) — 150 XP from Level 9 (Pro→Master!)
  // Total questions ~93 — just under 100 so after one 10-question session
  // he unlocks "Honderdtal" achievement + DJ Pixel mascot (testable!)
  const djayden = createDemoLearner(rng, 'Djayden', 8, 5850, 7, {
    streak: {
      currentTotalStreak: 7,
      longestTotalStreak: 18,
      lastStreakQuestionId: 'TIJD-ANALOOG_demo_q44',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 8,
      todayGoalReached: false,
      currentWeekDays: 3,
      consecutiveFullWeeks: 2,
      longestWeekStreak: 4,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 5,
        '2026-W09': 5,
        '2026-W08': 4,
        '2026-W07': 5,
        '2026-W06': 4,
        '2026-W05': 5,
        '2026-W04': 3,
      },
      totalDailyGoalsReached: 31,
      totalPerfectSessions: 3,
    },
    // ~93 total questions (under 100 threshold for Honderdtal/DJ Pixel)
    completedQuestions: {
      Tijd: 12,
      Geld: 10,
      Getalbegrip: 18,
      Basisrekenen: 14,
      Metriek: 15,
      SpellingWW: 10,
      SpellingRegel: 6,
      Taalbegrip: 5,
      Studievaardig: 3,
      Topo: 0,
    },
    // Pre-populated achievements matching current stats (prevents popup overload)
    // Missing: 'century' (needs 100 questions — he's at 93, will unlock after 1 session!)
    unlockedAchievements: [
      'first-steps', // 5 questions ✓
      'getting-started', // 50 questions ✓
      'three-days', // 3 weekdays ✓
      'week-champion', // 5 weekdays (from weeklyHistory) ✓
      'hot-streak', // 10 correct streak (longestTotalStreak: 18) ✓
      'unstoppable', // 25 correct streak — no (18 < 25)
      'level-5', // level 5 ✓ (level 8)
      'level-10', // level 10 — no (level 8)
      'goal-getter', // 10 daily goals ✓ (31)
      'consistent', // 50 daily goals — no (31)
    ].filter((id) => !['unstoppable', 'level-10', 'consistent'].includes(id)),
    // Power user — broad and strong, peaks in maths, lighter on Topo/study.
    masteryScores: buildPersonaMasteryForGroup(7 as GroupId, rng, {
      baseline: 80,
      spread: 10,
      coverage: 0.82,
      strongModules: ['Getalbegrip', 'Basisrekenen', 'Metriek'],
      weakModules: ['Topo', 'Studievaardig'],
    }),
  })

  // Emma — Taal-specialist
  const emma = createDemoLearner(rng, 'Emma', 5, 2100, 7, {
    streak: {
      currentTotalStreak: 3,
      longestTotalStreak: 9,
      lastStreakQuestionId: 'SWW-TT-STAM_demo_q39',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 5,
      todayGoalReached: false,
      currentWeekDays: 4,
      consecutiveFullWeeks: 1,
      longestWeekStreak: 2,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 4,
        '2026-W09': 5,
        '2026-W08': 3,
        '2026-W07': 4,
        '2026-W06': 5,
      },
      totalDailyGoalsReached: 21,
      totalPerfectSessions: 1,
    },
    completedQuestions: {
      SpellingWW: 40,
      SpellingRegel: 35,
      Taalbegrip: 30,
      Metriek: 15,
      Getalbegrip: 12,
      Basisrekenen: 12,
      Tijd: 5,
      Geld: 5,
      Studievaardig: 8,
      Topo: 5,
    },
    // 167 total questions, level 5, streak 9, 21 daily goals
    unlockedAchievements: [
      'first-steps',
      'getting-started',
      'century',
      'three-days',
      'level-5',
      'goal-getter',
    ],
    // Taal-specialist — peaks on spelling/language, noticeably weaker maths.
    masteryScores: buildPersonaMasteryForGroup(7 as GroupId, rng, {
      baseline: 60,
      spread: 12,
      coverage: 0.72,
      strongModules: ['SpellingWW', 'SpellingRegel', 'Taalbegrip'],
      weakModules: ['Getalbegrip', 'Basisrekenen', 'Metriek'],
    }),
  })

  // Noah — Balanced learner
  const noah = createDemoLearner(rng, 'Noah', 6, 3050, 7, {
    streak: {
      currentTotalStreak: 0,
      longestTotalStreak: 12,
      lastStreakQuestionId: 'GET-BREUK-BASIS_demo_q29',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 10,
      todayGoalReached: true,
      currentWeekDays: 4,
      consecutiveFullWeeks: 1,
      longestWeekStreak: 3,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 5,
        '2026-W09': 3,
        '2026-W08': 5,
        '2026-W07': 4,
        '2026-W06': 5,
        '2026-W05': 3,
      },
      totalDailyGoalsReached: 25,
      totalPerfectSessions: 2,
    },
    // 175 total questions, level 6, streak 12, 25 daily goals
    unlockedAchievements: [
      'first-steps',
      'getting-started',
      'century',
      'three-days',
      'hot-streak',
      'level-5',
      'goal-getter',
    ],
    completedQuestions: {
      Getalbegrip: 30,
      Basisrekenen: 20,
      Tijd: 13,
      Geld: 12,
      Metriek: 25,
      SpellingWW: 20,
      SpellingRegel: 18,
      Taalbegrip: 15,
      Studievaardig: 12,
      Topo: 10,
    },
    // Balanced learner — even profile across every subject.
    masteryScores: buildPersonaMasteryForGroup(7 as GroupId, rng, {
      baseline: 68,
      spread: 11,
      coverage: 0.76,
    }),
  })

  // Sophie — Top student
  const sophie = createDemoLearner(rng, 'Sophie', 9, 7800, 8, {
    streak: {
      currentTotalStreak: 12,
      longestTotalStreak: 32,
      lastStreakQuestionId: 'SWW-TT-STAM_demo_q49',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 12,
      todayGoalReached: true,
      currentWeekDays: 4,
      consecutiveFullWeeks: 9,
      longestWeekStreak: 9,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 5,
        '2026-W09': 5,
        '2026-W08': 5,
        '2026-W07': 5,
        '2026-W06': 5,
        '2026-W05': 5,
        '2026-W04': 5,
        '2026-W03': 5,
        '2026-W02': 5,
      },
      totalDailyGoalsReached: 45,
      totalPerfectSessions: 4,
    },
    // 318 total, level 9, streak 32, 9 consecutive weeks, 45 daily goals
    unlockedAchievements: [
      'first-steps',
      'getting-started',
      'century',
      'three-days',
      'week-champion',
      'fire-master',
      'hot-streak',
      'unstoppable',
      'legendary-streak',
      'level-5',
      'level-10',
      'goal-getter',
      'consistent',
    ],
    completedQuestions: {
      SpellingWW: 50,
      SpellingRegel: 45,
      Taalbegrip: 40,
      Getalbegrip: 38,
      Basisrekenen: 35,
      Tijd: 18,
      Geld: 17,
      Metriek: 30,
      Studievaardig: 25,
      Topo: 20,
    },
    // Top student — consistently high across the full group-8 band.
    masteryScores: buildPersonaMasteryForGroup(8 as GroupId, rng, {
      baseline: 92,
      spread: 6,
      coverage: 0.85,
    }),
  })

  // Liam — Worstelt, heeft extra aandacht nodig
  const liam = createDemoLearner(rng, 'Liam', 3, 600, 8, {
    streak: {
      currentTotalStreak: 1,
      longestTotalStreak: 4,
      lastStreakQuestionId: 'REK-TAFELS-BASIS_demo_q12',
    },
    dailyStreak: {
      dailyGoalTarget: 5,
      todayQuestionsAnswered: 3,
      todayGoalReached: false,
      currentWeekDays: 2,
      consecutiveFullWeeks: 0,
      longestWeekStreak: 1,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 3,
        '2026-W09': 2,
        '2026-W08': 1,
        '2026-W07': 3,
        '2026-W06': 2,
      },
      totalDailyGoalsReached: 11,
      totalPerfectSessions: 0,
    },
    // 87 total, level 3, streak 4, 11 daily goals
    unlockedAchievements: ['first-steps', 'getting-started', 'goal-getter'],
    completedQuestions: {
      Basisrekenen: 18,
      Getalbegrip: 15,
      Tijd: 10,
      Geld: 8,
      Metriek: 12,
      SpellingWW: 10,
      SpellingRegel: 5,
      Taalbegrip: 4,
      Studievaardig: 2,
      Topo: 3,
    },
    // Struggling learner — low across the board, marginally less weak on
    // foundational arithmetic. Sparse coverage (lots of un-attempted families).
    masteryScores: buildPersonaMasteryForGroup(8 as GroupId, rng, {
      baseline: 33,
      spread: 10,
      coverage: 0.5,
      strongModules: ['Basisrekenen'],
      weakModules: ['SpellingWW', 'Taalbegrip'],
    }),
  })

  // Noor — All-rounder
  const noor = createDemoLearner(rng, 'Noor', 8, 5900, 8, {
    streak: {
      currentTotalStreak: 5,
      longestTotalStreak: 23,
      lastStreakQuestionId: 'GET-GROOT-POS_demo_q34',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 7,
      todayGoalReached: false,
      currentWeekDays: 3,
      consecutiveFullWeeks: 2,
      longestWeekStreak: 4,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 4,
        '2026-W09': 5,
        '2026-W08': 5,
        '2026-W07': 3,
        '2026-W06': 5,
        '2026-W05': 4,
        '2026-W04': 5,
      },
      totalDailyGoalsReached: 31,
      totalPerfectSessions: 2,
    },
    // 218 total, level 8, streak 23, 31 daily goals
    unlockedAchievements: [
      'first-steps',
      'getting-started',
      'century',
      'three-days',
      'hot-streak',
      'level-5',
      'goal-getter',
    ],
    completedQuestions: {
      Getalbegrip: 35,
      Basisrekenen: 25,
      Metriek: 30,
      Tijd: 15,
      Geld: 13,
      SpellingWW: 25,
      SpellingRegel: 22,
      Taalbegrip: 20,
      Studievaardig: 18,
      Topo: 15,
    },
    // All-rounder — strong overall, peaks in maths/metriek.
    masteryScores: buildPersonaMasteryForGroup(8 as GroupId, rng, {
      baseline: 80,
      spread: 9,
      coverage: 0.8,
      strongModules: ['Getalbegrip', 'Basisrekenen', 'Metriek'],
    }),
  })

  // Yara — Math focus
  const yara = createDemoLearner(rng, 'Yara', 4, 1350, 6, {
    streak: {
      currentTotalStreak: 4,
      longestTotalStreak: 8,
      lastStreakQuestionId: 'MET-LEN-OMREKEN_demo_q29',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 6,
      todayGoalReached: false,
      currentWeekDays: 2,
      consecutiveFullWeeks: 0,
      longestWeekStreak: 1,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 3,
        '2026-W09': 5,
        '2026-W08': 4,
        '2026-W07': 2,
        '2026-W06': 3,
      },
      totalDailyGoalsReached: 17,
      totalPerfectSessions: 1,
    },
    // 108 total, level 4, streak 8, 17 daily goals
    unlockedAchievements: [
      'first-steps',
      'getting-started',
      'century',
      'three-days',
      'goal-getter',
    ],
    completedQuestions: {
      Metriek: 30,
      Tijd: 13,
      Geld: 12,
      Getalbegrip: 22,
      Basisrekenen: 15,
      SpellingWW: 8,
      SpellingRegel: 5,
      Taalbegrip: 3,
      Studievaardig: 0,
      Topo: 0,
    },
    // Math focus — strong in maths/metriek, weaker in language.
    masteryScores: buildPersonaMasteryForGroup(6 as GroupId, rng, {
      baseline: 60,
      spread: 11,
      coverage: 0.62,
      strongModules: ['Metriek', 'Getalbegrip', 'Basisrekenen'],
      weakModules: ['SpellingWW', 'SpellingRegel', 'Taalbegrip'],
    }),
  })

  // Sem — Topo & Studievaardig focus
  const sem = createDemoLearner(rng, 'Sem', 3, 750, 6, {
    streak: {
      currentTotalStreak: 2,
      longestTotalStreak: 5,
      lastStreakQuestionId: 'TOPO-NL-PROV_demo_q24',
    },
    dailyStreak: {
      dailyGoalTarget: 5,
      todayQuestionsAnswered: 3,
      todayGoalReached: false,
      currentWeekDays: 1,
      consecutiveFullWeeks: 0,
      longestWeekStreak: 1,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 2,
        '2026-W09': 3,
        '2026-W08': 1,
        '2026-W07': 2,
        '2026-W06': 3,
      },
      totalDailyGoalsReached: 11,
      totalPerfectSessions: 0,
    },
    // 103 total, level 3, streak 5, 11 daily goals
    unlockedAchievements: [
      'first-steps',
      'getting-started',
      'century',
      'goal-getter',
    ],
    completedQuestions: {
      Topo: 28,
      Studievaardig: 22,
      Tijd: 8,
      Geld: 7,
      Metriek: 12,
      Getalbegrip: 8,
      Basisrekenen: 8,
      SpellingWW: 5,
      SpellingRegel: 3,
      Taalbegrip: 2,
    },
    // Topo & study-skills focus — peaks there, weaker in maths.
    masteryScores: buildPersonaMasteryForGroup(6 as GroupId, rng, {
      baseline: 55,
      spread: 11,
      coverage: 0.55,
      strongModules: ['Topo', 'Studievaardig'],
      weakModules: ['Getalbegrip', 'Basisrekenen'],
    }),
  })

  // Demo Djay — single QA/showcase account with FULL ACCESS to everything:
  // every variant + accessory + tier (via maxed stats) AND every treatment
  // finish + standalone accessory (pre-persisted in unlockedCosmetics).
  // labSessionsCompleted is well past the highest treatment gate (25).
  //
  // GROUP CHOICE — grade 7 (group 7 = tiers 7–12): the collection's live
  // unlock checks are group-scoped, and group 8 spans ONLY tiers 13–16 (the
  // narrowest band — too few questions to clear the volume gates, and at least
  // one module has no tier-13–16 family so it can never read as mastered →
  // the "9/10 vakken" gap). Group 7's six-tier band has full module coverage
  // and a large enough question pool to deterministically complete the locker.
  // Grade 7 also matches the demo's classroom (Groep 7A, groupLevel 7),
  // removing the latent grade-8-in-a-group-7-class mismatch.
  const demo = createDemoLearner(rng, 'Demo Djay', 20, 99000, 7, {
    streak: {
      currentTotalStreak: 40,
      longestTotalStreak: 80,
      lastStreakQuestionId: 'GET-BREUK-BASIS_demo_q99',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 10,
      todayGoalReached: true,
      currentWeekDays: 5,
      consecutiveFullWeeks: 12,
      longestWeekStreak: 12,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 5,
        '2026-W09': 5,
        '2026-W08': 5,
        '2026-W07': 5,
        '2026-W06': 5,
        '2026-W05': 5,
        '2026-W04': 5,
        '2026-W03': 5,
      },
      totalDailyGoalsReached: 120,
      totalPerfectSessions: 30,
    },
    // Cumulative completed lab sessions — past every treatment gate.
    labSessionsCompleted: 30,
    // Pre-own every treatment finish + every standalone (gate-earned) accessory
    // so the locker shows full access immediately. Achievement-backed cosmetics
    // unlock automatically from the maxed stats / mastery below.
    unlockedCosmetics: [
      'glow',
      'gummy',
      'sparkle',
      'aura',
      'cap',
      'sunglasses',
      'goldChain',
    ],
    // Every achievement marked unlocked (suppresses celebration popups; the
    // cosmetic gates themselves read the maxed checkData below).
    unlockedAchievements: ACHIEVEMENTS.map((a) => a.id),
    // Answer the ENTIRE group-8 question pool of every module. The live
    // achievement counter (getAnsweredQuestionsForGroup) only counts real
    // questions whose authored group matches the active learner's group, so a
    // hand-picked per-module count could never be tuned to clear the
    // volume gates (5 / 50 / 100 / 250 / 500 / 1000) as content evolves.
    // Passing Infinity seeds an answered state for every group-8 question in
    // each module → totalQuestionsAnswered reads the full group-8 pool, which
    // deterministically unlocks every reachable "X vragen" achievement and can
    // never drift as seeds are added or removed.
    completedQuestions: {
      Getalbegrip: Infinity,
      Basisrekenen: Infinity,
      Tijd: Infinity,
      Geld: Infinity,
      Metriek: Infinity,
      SpellingWW: Infinity,
      SpellingRegel: Infinity,
      Taalbegrip: Infinity,
      Studievaardig: Infinity,
      Topo: Infinity,
    },
    // FULL mastery: every family eligible at group 7 is set high (96-99), so
    // every module's mastery aggregate reads ≥ 95% → masteredCount = all
    // modules. Combined with level 20, this unlocks the Master tier and every
    // mastery-gated look (Frost / Royal / Rainbow). Generated from the taxonomy
    // so it can never drift out of sync as families are added (was previously a
    // hand-picked subset that left some modules below the mastered threshold).
    // Must match the learner's group (7) so the eligible-family filter lines up
    // with the group-scoped mastery aggregator.
    masteryScores: buildCompleteMasteryForGroup(7 as GroupId, rng),
  })

  // Make a handful of Demo Djay's mastered families read as "due for review"
  // (last practised > 7 days ago) WITHOUT lowering their mastery score. Decay
  // is computed on-read purely from `lastSeen`, while every module-mastery
  // aggregate and collectible gate reads the RAW `masteryScore` — so the
  // locker stays 100% complete, but the home screen's smart-session engine now
  // has a "Herhalen" pick to surface. A perfectly-complete account otherwise
  // has nothing to recommend, which is correct but leaves the showcase on the
  // bare "Geen tip voor vandaag — kies zelf!" fallback. Deterministic: we pick
  // the first few families in insertion order (stable across loads).
  const STALE_REVIEW_DAYS = 14
  const staleSeenAt = Date.now() - STALE_REVIEW_DAYS * 24 * 60 * 60 * 1000
  Object.keys(demo.data.familyMastery)
    .slice(0, 5)
    .forEach((key) => {
      const state = demo.data.familyMastery[key]
      if (state) state.lastSeen = staleSeenAt
    })

  return { djayden, emma, noah, sophie, liam, noor, yara, sem, demo }
}
